// Google Maps Places API utility functions using JavaScript API

declare global {
  interface Window {
    google: any;
    initGoogleMaps: () => void;
  }
}

let isGoogleMapsLoaded = false;
let googleMapsPromise: Promise<void> | null = null;

/**
 * Load Google Maps JavaScript API
 */
const loadGoogleMapsAPI = (): Promise<void> => {
  if (isGoogleMapsLoaded) {
    return Promise.resolve();
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps) {
      isGoogleMapsLoaded = true;
      resolve();
      return;
    }

    // Create callback function
    window.initGoogleMaps = () => {
      isGoogleMapsLoaded = true;
      resolve();
    };

    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

/**
 * Fetch location suggestions using Google Places Autocomplete Service
 * @param query - The search query
 * @returns Promise with array of location suggestions
 */
export const fetchLocationSuggestions = async (query: string): Promise<string[]> => {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    // Load Google Maps API if not already loaded
    await loadGoogleMapsAPI();

    return new Promise((resolve) => {
      const service = new window.google.maps.places.AutocompleteService();
      
      service.getPlacePredictions(
        {
          input: query,
          types: ['(cities)'], // Focus on cities
          language: 'en', // English results
        },
        (predictions: any[], status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            const suggestions = predictions.map(prediction => prediction.description);
            resolve(suggestions);
          } else {
            // Return empty array on error or no results
            resolve([]);
          }
        }
      );
    });
  } catch (error) {
    console.error('Error fetching location suggestions:', error);
    return [];
  }
};

/**
 * Get place details by place ID using Places Service
 * @param placeId - Google Places place ID
 * @returns Promise with place details
 */
export const getPlaceDetails = async (placeId: string) => {
  try {
    await loadGoogleMapsAPI();

    return new Promise((resolve, reject) => {
      // Create a temporary div for the PlacesService (required by Google Maps API)
      const tempDiv = document.createElement('div');
      const service = new window.google.maps.places.PlacesService(tempDiv);

      service.getDetails(
        {
          placeId: placeId,
          fields: ['name', 'formatted_address', 'geometry', 'place_id'],
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place);
          } else {
            reject(new Error(`Places service failed with status: ${status}`));
          }
        }
      );
    });
  } catch (error) {
    console.error('Error fetching place details:', error);
    throw error;
  }
};

/**
 * Check if Google Maps API is loaded
 */
export const isGoogleMapsAPILoaded = (): boolean => {
  return isGoogleMapsLoaded && window.google && window.google.maps;
};